import { useEffect, useRef, useState } from "react";

const PLAYLIST = [
  "/website-music-1.mp3",
  "/website-music-2.mp3",
  "/website-music-3.mp3",
];

/**
 * Floating background-music player. Loops a 25%-volume playlist site-wide,
 * advancing to the next track each time one ends.
 *
 * Autoplay reality: Chrome / Safari / Firefox block unmuted audio playback
 * until the page has registered a user gesture. We try to play on mount; if
 * blocked, we attach a one-shot `pointerdown` listener so the track starts the
 * moment the user clicks anywhere on the page — no extra "click to play"
 * prompt needed. The Skip and Pause buttons remain available either way.
 */
export function MusicPlayer() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [trackIndex, setTrackIndex] = useState(() =>
    Math.floor(Math.random() * PLAYLIST.length),
  );
  const [playing, setPlaying] = useState(false);

  // One-time autoplay attempt on mount; falls back to the next user gesture.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.25;
    let cancelled = false;
    let removeGesture: (() => void) | null = null;

    const start = async () => {
      try {
        await audio.play();
        if (!cancelled) setPlaying(true);
      } catch {
        if (cancelled) return;
        const onGesture = async () => {
          try {
            await audio.play();
            setPlaying(true);
          } catch {
            /* user can still toggle manually */
          }
        };
        window.addEventListener("pointerdown", onGesture, { once: true });
        removeGesture = () =>
          window.removeEventListener("pointerdown", onGesture);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (removeGesture) removeGesture();
    };
  }, []);

  // Whenever the source changes (skip / track ended), keep playing if we were.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.25;
    if (playing) {
      audio.play().catch(() => {
        /* ignore — gesture listener will pick it up */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackIndex]);

  function next() {
    setTrackIndex((i) => (i + 1) % PLAYLIST.length);
  }

  function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      audio.pause();
      setPlaying(false);
    }
  }

  return (
    <>
      <audio
        ref={audioRef}
        src={PLAYLIST[trackIndex]}
        preload="auto"
        onEnded={next}
      />
      <div className="music-player-group" data-no-sound>
        <button
          type="button"
          className={`music-player${playing ? " is-playing" : ""}`}
          onClick={toggle}
          data-no-sound
          title={playing ? "Pause background music" : "Play background music"}
          aria-label={playing ? "Pause background music" : "Play background music"}
        >
          <span aria-hidden="true">{playing ? "♫" : "▶"}</span>
        </button>
        <button
          type="button"
          className="music-skip"
          onClick={next}
          data-no-sound
          title="Skip to next track"
          aria-label="Skip to next track"
        >
          <span aria-hidden="true">⏭</span>
        </button>
      </div>
    </>
  );
}
